import { Role, SubscriptionStatus } from '@form-factory/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Button } from '@utima/ui';
import { CreditCard } from 'lucide-react';

import { FormPage } from '@/components/page/FormPage';
import { Protected } from '@/components/protected/Protected';
import {
  AddBalanceForm,
  OrganizationForm,
  OrganizationUsageReport,
} from '@/features/organizations';
import { OverrideLimitsForm } from '@/features/organizations/components/OverrideLimitsForm';
import { trpc } from '@/services/trpc';

const populate = ['billingInfo', 'workingHours', 'subscriptions', 'tierConfig'];

export const Route = createFileRoute(
  '/_baseLayout/organizations/$organizationId/edit',
)({
  component: OrganizationsEditPage,
  beforeLoad: async ({
    context: { accessGuard },
    params: { organizationId },
  }) => accessGuard([Role.SuperAdmin, Role.Admin], organizationId),
  loader: async ({ context: { trpcUtils }, params: { organizationId } }) => {
    await trpcUtils.organizations.findOne.ensureData({ id: organizationId });
  },
});

export function OrganizationsEditPage() {
  const { organizationId } = Route.useParams();
  const { t } = useLingui();
  const [data] = trpc.organizations.findOne.useSuspenseQuery({
    id: organizationId,
    populate,
  });

  const currentSubscription = data.subscriptions.find(
    sub => sub.status === SubscriptionStatus.Active,
  );

  return (
    <FormPage
      title={`${t`Edit Organization`} - ${data.name}`}
      breadcrumbs={[
        {
          label: t`Organizations`,
          to: '/organizations',
        },
        {
          label: data.name,
          to: '/organizations/$organizationId/edit',
          params: { organizationId },
        },
      ]}
      actions={
        <Protected roles={[Role.SuperAdmin]}>
          {/* <Button
            data-cta='admin-organizations-edit-manage-custom-plan-button'
            variant='secondary'
            asChild
          >
            <Link
              to='/organizations/$organizationId/plan-management'
              params={{ organizationId }}
            >
              <Wrench className='size-4' />
              <Trans>Manage Custom Plan</Trans>
            </Link>
          </Button> */}
          <Button
            data-cta='admin-organizations-edit-manage-payments-button'
            variant='secondary'
            asChild
            icon
          >
            <Link
              to='/organizations/$organizationId/payments'
              params={{ organizationId }}
            >
              <CreditCard className='size-4' />
              <Trans>Manage Payments</Trans>
            </Link>
          </Button>
        </Protected>
      }
    >
      <OrganizationForm organizationId={organizationId} data={data} />
      {data && (
        <Protected roles={[Role.SuperAdmin]}>
          <div className='flex grow flex-row gap-2'>
            <AddBalanceForm data={data} />
            {currentSubscription && (
              <OverrideLimitsForm
                organizationId={data.id}
                currentSubscription={currentSubscription}
              />
            )}
          </div>
          <OrganizationUsageReport
            organizationId={organizationId}
            currency={data.currency}
          />
        </Protected>
      )}
    </FormPage>
  );
}
